import {
  del,
  get,
  isCancelError,
  patch,
  post,
} from 'aws-amplify/api'
import { getApiAuthorizationHeader } from 'shared/apiAuthHeader'

type LegacyOptions = {
  body?: unknown
  headers?: Record<string, string>
  queryStringParameters?: Record<string, string | number | boolean | undefined>
}

type RestOperation = {
  response: Promise<any>
  cancel(message?: string): void
}

const operations = new WeakMap<Promise<unknown>, { cancel(message?: string): void }>()

async function parse(operation: RestOperation): Promise<unknown> {
  const response = await operation.response
  return response.body.json()
}

function run(factory: (requestOptions: any) => RestOperation, input?: LegacyOptions) {
  let operation: RestOperation | undefined
  const promise = options(input).then((requestOptions) => {
    operation = factory(requestOptions)
    return parse(operation)
  })
  operations.set(promise, { cancel: (message) => operation?.cancel(message) })
  return promise
}

async function options(input: LegacyOptions = {}) {
  const authHeaders = await getApiAuthorizationHeader()
  return {
    body: input.body as any,
    headers: { ...authHeaders, ...input.headers },
    queryParams: Object.fromEntries(
      Object.entries(input.queryStringParameters ?? {})
        .filter(([, value]) => value !== undefined && value !== null)
        .map(([key, value]) => [key, String(value)])
    ),
  }
}

const API = {
  get(apiName: string, path: string, input?: any): Promise<any> {
    return run((requestOptions) => get({ apiName, path, options: requestOptions }), input)
  },
  post(apiName: string, path: string, input?: any): Promise<any> {
    return run((requestOptions) => post({ apiName, path, options: requestOptions }), input)
  },
  patch(apiName: string, path: string, input?: any): Promise<any> {
    return run((requestOptions) => patch({ apiName, path, options: requestOptions }), input)
  },
  del(apiName: string, path: string, input?: any): Promise<any> {
    return run((requestOptions) => del({ apiName, path, options: requestOptions }), input)
  },
  cancel(promise: Promise<unknown>, message?: string) {
    operations.get(promise)?.cancel(message)
  },
  isCancel: isCancelError,
}

export default API
